import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Grid,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Radio,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CallSplit as CallSplitIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { el } from 'date-fns/locale';

// Services
import invoiceService from '../services/invoiceService';
import clientService from '../../clients/services/clientService';
import serviceService from '../../services/services/serviceService';
import { formatServiceSelectionTitle, sortServicesNewestFirst } from '../../services/utils/serviceDisplayHelpers';
import ClientSelect from '../../../components/common/ClientSelect';


const InvoiceFormModal = ({ 
  open, 
  onClose, 
  invoice = null, 
  onSuccess,
  mode = 'create', // 'create', 'edit', 'createFromServices'
  preSelectedData = null // For createFromServices mode
}) => {
  // State
  const [formData, setFormData] = useState({
    client_id: '',
    quote_id: null,
    invoice_type: 'service_invoice_no_holdings',
    invoice_number: '', // For proforma invoices
    reference_number: '',
    title: '',
    issue_date: new Date().toISOString().split('T')[0],
    payment_method: 'bank_transfer',
    notes: '',
    default_vat_rate: 24,
    invoice_items: [],
    // Alternative billing information
    client_billing_info_id: null, // Selected billing info set from client
    billing_vat_number: '',
    billing_tax_office: '',
    billing_business_name: '',
    billing_profession: '',
    billing_address: '',
    billing_postal_code: ''
  });
  const [showBillingInfo, setShowBillingInfo] = useState(false);
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Data
  const [services, setServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [clientServices, setClientServices] = useState([]);
  const [clientBillingInfo, setClientBillingInfo] = useState([]); // Billing info sets for selected client
  const [creditableInvoices, setCreditableInvoices] = useState([]); // Για πιστωτικό: τιμολόγια που μπορούν να γίνουν πίστωση
  const [selectedReferenceInvoiceId, setSelectedReferenceInvoiceId] = useState(null);

  // Ομαδοποίηση επιλεγμένων υπηρεσιών σε μία γραμμή (μόνο εμφάνιση· οι υπηρεσίες μένουν συνδεδεμένες)
  const [mergeServices, setMergeServices] = useState(false);
  const [mergeTitle, setMergeTitle] = useState('');
  // Γραμμές τιμολογίου: ποιες ομάδες είναι ανοιχτές + ποιες μεμονωμένες γραμμές επιλέχθηκαν για ομαδοποίηση
  const [expandedGroups, setExpandedGroups] = useState({});
  const [groupSelection, setGroupSelection] = useState([]);

  // Load data
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && invoice) {
        // Load full invoice details to include invoice_items
        (async () => {
          try {
            setLoading(true);
            const full = await invoiceService.getInvoice(invoice.id);
            // Check if items have custom taxable amount (αν το vat_amount δεν αντιστοιχεί στο subtotal * vat_rate)
            const processedItems = (full.invoice_items || []).map(item => {
              const subtotal = (item.unit_price || 0) * (item.quantity || 1);
              const expectedVat = subtotal * ((item.vat_rate || 24) / 100);
              const actualVat = item.vat_amount || 0;
              // Αν η διαφορά είναι > 0.01€, τότε έχει custom taxable amount
              const isCustom = Math.abs(actualVat - expectedVat) > 0.01;
              // Υπολογίζουμε το custom_taxable_amount: vat_amount / vat_rate * 100
              const customTaxable = isCustom && item.vat_amount > 0 
                ? item.vat_amount / ((item.vat_rate || 24) / 100)
                : null;
              
              
              return {
                ...item,
                is_custom_taxable: isCustom,
                custom_taxable_amount: customTaxable
              };
            });
            
            setFormData({
              client_id: full.client_id || '',
              quote_id: full.quote_id || null,
              invoice_number: full.invoice_number || '', // display only
              invoice_type: full.invoice_type || 'service_invoice_no_holdings',
              reference_number: full.reference_number || '',
              title: full.title || '',
              issue_date: full.issue_date || new Date().toISOString().split('T')[0],
              payment_method: full.payment_method || 'bank_transfer',
              notes: full.notes || '',
              default_vat_rate: full.vat_rate || 24,
              invoice_items: processedItems,
              // Alternative billing information
              client_billing_info_id: full.client_billing_info_id || null,
              billing_vat_number: full.billing_vat_number || '',
              billing_tax_office: full.billing_tax_office || '',
              billing_business_name: full.billing_business_name || '',
              billing_profession: full.billing_profession || '',
              billing_address: full.billing_address || '',
              billing_postal_code: full.billing_postal_code || ''
            });
            // Load billing info for client to show in dropdown
            if (full.client_id) {
              loadClientBillingInfo(full.client_id);
            }
            setShowBillingInfo(!!(full.billing_vat_number || full.billing_tax_office || full.billing_business_name || full.billing_profession || full.billing_address || full.billing_postal_code));
          } catch (err) {
            console.error('Error loading invoice details:', err);
            // fallback to whatever we have
            setFormData({
              client_id: invoice.client_id || '',
              quote_id: invoice.quote_id || null,
              invoice_number: invoice.invoice_number || '',
              invoice_type: invoice.invoice_type || 'service_invoice_no_holdings',
              reference_number: invoice.reference_number || '',
              title: invoice.title || '',
              issue_date: invoice.issue_date || new Date().toISOString().split('T')[0],
              payment_method: invoice.payment_method || 'bank_transfer',
              notes: invoice.notes || '',
              default_vat_rate: invoice.vat_rate || 24,
              invoice_items: (invoice.invoice_items || []).map(item => {
                const subtotal = (item.unit_price || 0) * (item.quantity || 1);
                const expectedVat = subtotal * ((item.vat_rate || 24) / 100);
                const actualVat = item.vat_amount || 0;
                const isCustom = Math.abs(actualVat - expectedVat) > 0.01;
                const customTaxable = isCustom && item.vat_amount > 0 
                  ? item.vat_amount / ((item.vat_rate || 24) / 100)
                  : null;
                return { 
                  ...item, 
                  is_custom_taxable: isCustom,
                  custom_taxable_amount: customTaxable
                };
              }),
              // Alternative billing information
              client_billing_info_id: invoice.client_billing_info_id || null,
              billing_vat_number: invoice.billing_vat_number || '',
              billing_tax_office: invoice.billing_tax_office || '',
              billing_business_name: invoice.billing_business_name || '',
              billing_profession: invoice.billing_profession || '',
              billing_address: invoice.billing_address || '',
              billing_postal_code: invoice.billing_postal_code || ''
            });
          } finally {
            setLoading(false);
          }
        })();
        setSelectedServices([]);
        setClientServices([]);
      } else if (mode === 'createFromServices' && preSelectedData) {
        // Create from pre-selected services
        setFormData({
          client_id: preSelectedData.client_id || '',
          quote_id: null,
          invoice_type: 'service_invoice_no_holdings',
          invoice_number: '',
          reference_number: '',
          title: 'Τιμολόγιο παροχής υπηρεσιών',
          issue_date: new Date().toISOString().split('T')[0],
          payment_method: 'bank_transfer',
          notes: '',
          default_vat_rate: 24,
          invoice_items: [],
          // Alternative billing information
          client_billing_info_id: null,
          billing_vat_number: '',
          billing_tax_office: '',
          billing_business_name: '',
          billing_profession: '',
          billing_address: '',
          billing_postal_code: ''
        });
        setSelectedServices(preSelectedData.services || []);
        setClientServices(preSelectedData.services || []);
      } else {
        // Create new invoice
        setFormData({
          client_id: '',
          quote_id: null,
          invoice_type: 'service_invoice_no_holdings',
          invoice_number: '',
          reference_number: '',
          title: 'Τιμολόγιο παροχής υπηρεσιών',
          issue_date: new Date().toISOString().split('T')[0],
          payment_method: 'bank_transfer',
          notes: '',
          default_vat_rate: 24,
          invoice_items: [],
          // Alternative billing information
          client_billing_info_id: null,
          billing_vat_number: '',
          billing_tax_office: '',
          billing_business_name: '',
          billing_profession: '',
          billing_address: '',
          billing_postal_code: ''
        });
        setSelectedServices([]);
        setClientServices([]);
      }
      
      setErrors({});
      setMergeServices(false);
      setMergeTitle('');
      setExpandedGroups({});
      setGroupSelection([]);
    }
  }, [open, invoice, mode, preSelectedData]);

  useEffect(() => {
    if (formData.client_id && mode === 'createFromServices') {
      if (formData.invoice_type === 'cancel_invoice') {
        loadCreditableInvoices(formData.client_id);
      } else {
        loadClientServices(formData.client_id);
      }
    } else {
      setClientServices([]);
      setCreditableInvoices([]);
      setSelectedReferenceInvoiceId(null);
    }
    if (formData.client_id) {
      loadClientBillingInfo(formData.client_id);
    } else {
      setClientBillingInfo([]);
    }
  }, [formData.client_id, formData.invoice_type, mode]);

  // Reset selection when switching type
  useEffect(() => {
    if (formData.invoice_type !== 'cancel_invoice') {
      setSelectedReferenceInvoiceId(null);
      setCreditableInvoices([]);
    }
  }, [formData.invoice_type]);

  // Load client billing info
  const loadClientBillingInfo = async (clientId) => {
    try {
      const result = await clientService.getBillingInfo(clientId);
      if (result.success && result.data) {
        setClientBillingInfo(result.data);
        
        // Auto-select default billing info if available and not editing
        if (mode !== 'edit') {
          const defaultBillingInfo = result.data.find(info => info.is_default && info.is_active);
          if (defaultBillingInfo) {
            // Use setTimeout to ensure state is updated
            setTimeout(() => {
              handleBillingInfoSelectFromData(defaultBillingInfo, result.data);
            }, 0);
          }
        }
      } else {
        setClientBillingInfo([]);
      }
    } catch (error) {
      console.error('Error loading client billing info:', error);
      setClientBillingInfo([]);
    }
  };

  // Helper to select billing info with data (to avoid dependency issues)
  const handleBillingInfoSelectFromData = (selectedInfo, billingInfoArray) => {
    if (selectedInfo) {
      setFormData(prev => ({
        ...prev,
        client_billing_info_id: selectedInfo.id,
        billing_vat_number: selectedInfo.vat_number || '',
        billing_tax_office: selectedInfo.tax_office || '',
        billing_business_name: selectedInfo.business_name || '',
        billing_profession: selectedInfo.profession || '',
        billing_address: selectedInfo.address || '',
        billing_postal_code: selectedInfo.postal_code || ''
      }));
      setShowBillingInfo(true);
    }
  };

  // Handle billing info selection
  const handleBillingInfoSelect = (billingInfoId) => {
    if (!billingInfoId || billingInfoId === '') {
      // Clear custom fields
      setFormData(prev => ({
        ...prev,
        client_billing_info_id: null,
        billing_vat_number: '',
        billing_tax_office: '',
        billing_business_name: '',
        billing_profession: '',
        billing_address: '',
        billing_postal_code: ''
      }));
      return;
    }

    const selectedInfo = clientBillingInfo.find(info => info.id === parseInt(billingInfoId));
    if (selectedInfo) {
      setFormData(prev => ({
        ...prev,
        client_billing_info_id: parseInt(billingInfoId),
        billing_vat_number: selectedInfo.vat_number || '',
        billing_tax_office: selectedInfo.tax_office || '',
        billing_business_name: selectedInfo.business_name || '',
        billing_profession: selectedInfo.profession || '',
        billing_address: selectedInfo.address || '',
        billing_postal_code: selectedInfo.postal_code || ''
      }));
      setShowBillingInfo(true);
    }
  };

  const loadClientServices = async (clientId) => {
    try {
      setLoading(true);
      // Όλες οι υπηρεσίες του πελάτη· φίλτρο μόνο «χωρίς ενεργό τιμολόγιο», ανεξάρτητα status (εκκρεμής κλπ.)
      const response = await serviceService.getAll({
        client_id: clientId,
        limit: 500
      });
      if (response.success) {
        const allServices = response.data || [];
        const uninvoicedServices = allServices.filter(service =>
          !service.invoice_numbers ||
          (Array.isArray(service.invoice_numbers) && service.invoice_numbers.length === 0)
        );
        // Αποφεύγουμε duplicates με βάση το ID
        const uniqueServices = uninvoicedServices.filter((service, index, self) =>
          index === self.findIndex((s) => s.id === service.id)
        );
        const sortedServices = sortServicesNewestFirst(uniqueServices);
        setClientServices(sortedServices);
        
        if (preSelectedData) {
          const preSelectedServiceIds = preSelectedData.services.map(s => s.id);
          const selectedServices = sortedServices.filter(service => 
            preSelectedServiceIds.includes(service.id)
          );
          setSelectedServices(selectedServices);
        }
      } else {
        setClientServices([]);
      }
    } catch (err) {
      console.error('Error loading client services:', err);
      setClientServices([]);
    } finally {
      setLoading(false);
    }
  };

  const loadCreditableInvoices = async (clientId) => {
    try {
      setLoading(true);
      const data = await invoiceService.getCreditableInvoices(clientId);
      setCreditableInvoices(Array.isArray(data) ? data : []);
      setSelectedReferenceInvoiceId(null);
    } catch (err) {
      console.error('Error loading creditable invoices:', err);
      setCreditableInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  // Handlers
  const handleInputChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      // Auto-generate title based on invoice type
      if (field === 'invoice_type') {
        if (value === 'service_invoice' || value === 'service_invoice_no_holdings') {
          newData.title = 'Τιμολόγιο παροχής υπηρεσιών';
        } else if (value === 'sales_invoice') {
          newData.title = 'Τιμολόγιο πώλησης';
        } else if (value === 'cancel_invoice') {
          newData.title = 'Πιστωτικό σημείωμα';
        } else if (value === 'receipt') {
          newData.title = 'Απόδειξη εισπραξης';
        } else if (value === 'proforma_invoice') {
          newData.title = 'Προτιμολόγιο';
        }
      }
      
      // Update default VAT rate for new items if changed
      if (field === 'default_vat_rate') {
        // Optionally update existing items that haven't been customized
        // For now, we only affect new items
      }
      
      return newData;
    });
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleServiceToggle = (service) => {
    const isSelected = selectedServices.some(s => s.id === service.id);
    if (isSelected) {
      setSelectedServices(prev => prev.filter(s => s.id !== service.id));
    } else {
      // Προσθέτουμε μόνο αν δεν υπάρχει ήδη
      setSelectedServices(prev => {
        if (prev.some(s => s.id === service.id)) {
          return prev; // Already exists, don't add duplicate
        }
        return [...prev, service];
      });
    }
  };

  const handleSelectAll = () => {
    if (selectedServices.length === clientServices.length) {
      setSelectedServices([]);
    } else {
      setSelectedServices([...clientServices]);
    }
  };

  const handleAddItem = () => {
    const newItem = {
      service_id: null,
      name: '',
      description: '',
      quantity: 1,
      unit_price: 0,
      vat_rate: formData.default_vat_rate || 24,
      sort_order: formData.invoice_items.length
    };
    setFormData(prev => ({
      ...prev,
      invoice_items: [...prev.invoice_items, newItem]
    }));
  };

  const handleUpdateItem = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      invoice_items: prev.invoice_items.map((item, i) => {
        if (i === index) {
          const updatedItem = { ...item, [field]: value };
          const subtotal = (updatedItem.unit_price || 0) * (updatedItem.quantity || 1);
          
          // Αν είναι edit mode και αλλάζουμε custom_taxable_amount (φορολογητέο ποσό)
          if (mode === 'edit' && field === 'custom_taxable_amount') {
            const taxableAmount = parseFloat(value) || 0;
            updatedItem.custom_taxable_amount = taxableAmount; // Αποθήκευση της τιμής
            // ΦΠΑ = φορολογητέο ποσό * vat_rate
            updatedItem.vat_amount = taxableAmount * ((updatedItem.vat_rate || 24) / 100);
            // Total = subtotal (καθαρό κόστος) + vat_amount
            updatedItem.total_price = subtotal + updatedItem.vat_amount;
            updatedItem.is_custom_taxable = true; // Flag για custom
          }
          // Αν αλλάζουμε quantity (edit mode)
          else if (mode === 'edit' && field === 'quantity') {
            // Αν έχουμε custom taxable, υπολογίζουμε ξανά
            if (updatedItem.is_custom_taxable && updatedItem.custom_taxable_amount) {
              const taxableAmount = updatedItem.custom_taxable_amount;
              updatedItem.vat_amount = taxableAmount * ((updatedItem.vat_rate || 24) / 100);
              updatedItem.total_price = subtotal + updatedItem.vat_amount;
            } else {
              // Κανονικός υπολογισμός
              const vatAmount = subtotal * ((updatedItem.vat_rate || 24) / 100);
              updatedItem.vat_amount = vatAmount;
              updatedItem.total_price = subtotal + vatAmount;
            }
          }
          // Αν αλλάζουμε vat_rate (edit mode)
          else if (mode === 'edit' && field === 'vat_rate') {
            if (updatedItem.is_custom_taxable && updatedItem.custom_taxable_amount) {
              const taxableAmount = updatedItem.custom_taxable_amount;
              updatedItem.vat_amount = taxableAmount * ((updatedItem.vat_rate || 24) / 100);
              updatedItem.total_price = subtotal + updatedItem.vat_amount;
            } else {
              const vatAmount = subtotal * ((updatedItem.vat_rate || 24) / 100);
              updatedItem.vat_amount = vatAmount;
              updatedItem.total_price = subtotal + vatAmount;
            }
          }
          // Create mode - κανονικός υπολογισμός
          else if (mode !== 'edit' && (field === 'unit_price' || field === 'quantity' || field === 'vat_rate')) {
            const itemSubtotal = (updatedItem.unit_price || 0) * (updatedItem.quantity || 1);
            const vatAmount = itemSubtotal * ((updatedItem.vat_rate || 24) / 100);
            updatedItem.vat_amount = vatAmount;
            updatedItem.total_price = itemSubtotal + vatAmount;
            updatedItem.is_custom_taxable = false;
          }
          
          return updatedItem;
        }
        return item;
      })
    }));
  };

  const handleRemoveItem = (index) => {
    setFormData(prev => ({
      ...prev,
      invoice_items: prev.invoice_items.filter((_, i) => i !== index)
    }));
  };

  // Προεπιλεγμένος τίτλος ομαδοποίησης: το όνομα της μεγαλύτερης (σε αξία) υπηρεσίας
  const getDefaultMergeTitle = () => {
    if (!selectedServices.length) return '';
    const biggest = [...selectedServices].sort(
      (a, b) => ((b?.price || 0) * (b?.quantity || 1)) - ((a?.price || 0) * (a?.quantity || 1))
    )[0];
    return biggest?.name || '';
  };

  const canMergeServices = selectedServices.length >= 2;

  const handleMergeToggle = (enabled) => {
    setMergeServices(enabled);
    if (enabled && !mergeTitle) {
      setMergeTitle(getDefaultMergeTitle());
    }
  };

  const calculateItemTotal = (item) => {
    const unitPrice = Number(item?.unit_price) || 0;
    const quantity = Number(item?.quantity) || 1;
    const vatRate = (Number(item?.vat_rate) || 24) / 100;
    const subtotal = unitPrice * quantity;
    const vatAmount = subtotal * vatRate;
    const total = subtotal + vatAmount;
    return Number.isFinite(total) ? total : 0;
  };

  // --- Ομαδοποίηση γραμμών (ίδιο group_key => μία γραμμή) ---

  const buildInvoiceRows = (items) => {
    const rows = [];
    const groupRowIndex = {};
    (items || []).forEach((item, index) => {
      const groupKey = item.group_key;
      if (!groupKey) {
        rows.push({ type: 'single', key: `single-${index}`, index, item });
        return;
      }
      if (groupRowIndex[groupKey] !== undefined) {
        rows[groupRowIndex[groupKey]].items.push({ item, index });
        return;
      }
      groupRowIndex[groupKey] = rows.length;
      rows.push({ type: 'group', key: `group-${groupKey}`, groupKey, items: [{ item, index }] });
    });
    // Ομάδα με ένα μόνο item εμφανίζεται ως απλή γραμμή
    return rows.map((row) => (
      row.type === 'group' && row.items.length === 1
        ? { type: 'single', key: row.key, index: row.items[0].index, item: row.items[0].item }
        : row
    ));
  };

  const summarizeGroup = (groupItems) => {
    const summary = groupItems.reduce((acc, { item }) => {
      const net = (Number(item.unit_price) || 0) * (Number(item.quantity) || 1);
      const total = item.total_price !== undefined && item.total_price !== null
        ? Number(item.total_price)
        : calculateItemTotal(item);
      acc.quantity += Number(item.quantity) || 1;
      acc.net += net;
      acc.total += total;
      acc.vatRates.add(Number(item.vat_rate) || 0);
      return acc;
    }, { quantity: 0, net: 0, total: 0, vatRates: new Set() });
    return {
      quantity: summary.quantity,
      net: summary.net,
      total: summary.total,
      vat: summary.total - summary.net,
      vatRate: summary.vatRates.size === 1 ? [...summary.vatRates][0] : null
    };
  };

  const toggleGroupExpanded = (groupKey) => {
    setExpandedGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  const handleUpdateGroupTitle = (groupKey, value) => {
    setFormData((prev) => ({
      ...prev,
      invoice_items: prev.invoice_items.map((item) => (
        item.group_key === groupKey ? { ...item, group_title: value } : item
      ))
    }));
  };

  const handleUngroup = (groupKey) => {
    setFormData((prev) => ({
      ...prev,
      invoice_items: prev.invoice_items.map((item) => (
        item.group_key === groupKey ? { ...item, group_key: null, group_title: null } : item
      ))
    }));
    setExpandedGroups((prev) => {
      const next = { ...prev };
      delete next[groupKey];
      return next;
    });
  };

  const handleToggleGroupSelection = (index) => {
    setGroupSelection((prev) => (
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    ));
  };

  const handleGroupSelected = () => {
    if (groupSelection.length < 2) return;
    const groupKey = (window.crypto && window.crypto.randomUUID)
      ? window.crypto.randomUUID()
      : `grp-${Date.now()}`;
    const selected = formData.invoice_items.filter((_, i) => groupSelection.includes(i));
    const biggest = [...selected].sort(
      (a, b) => ((Number(b.unit_price) || 0) * (Number(b.quantity) || 1))
        - ((Number(a.unit_price) || 0) * (Number(a.quantity) || 1))
    )[0];
    const title = biggest?.name || 'Ομαδοποιημένη γραμμή';
    setFormData((prev) => ({
      ...prev,
      invoice_items: prev.invoice_items.map((item, i) => (
        groupSelection.includes(i)
          ? { ...item, group_key: groupKey, group_title: title }
          : item
      ))
    }));
    setGroupSelection([]);
    setExpandedGroups((prev) => ({ ...prev, [groupKey]: false }));
  };

  // Μία μεμονωμένη γραμμή τιμολογίου (ομαδοποιημένα items την εμφανίζουν με εσοχή)
  const renderItemRow = (item, index, { grouped = false } = {}) => (
    <TableRow key={`item-${index}`} sx={grouped ? { '& td:first-of-type': { pl: 6 } } : undefined}>
      <TableCell>
        {mode === 'edit' ? (
          <Typography variant="body2">{item.name}</Typography>
        ) : (
          <TextField
            size="small"
            fullWidth
            value={item.name || ''}
            onChange={(e) => handleUpdateItem(index, 'name', e.target.value)}
            placeholder="Όνομα υπηρεσίας"
          />
        )}
      </TableCell>
      <TableCell align="right">
        <TextField
          size="small"
          type="number"
          value={item.quantity || 1}
          onChange={(e) => handleUpdateItem(index, 'quantity', parseInt(e.target.value) || 1)}
          sx={{ width: 80 }}
          inputProps={{ min: 1 }}
        />
      </TableCell>
      <TableCell align="right">
        {mode === 'edit' ? (
          <Typography variant="body2" color="text.secondary">
            {invoiceService.formatCurrency(item.unit_price || 0)}
            <Typography variant="caption" display="block" color="text.secondary">
              (καθαρό)
            </Typography>
          </Typography>
        ) : (
          <TextField
            size="small"
            type="number"
            value={item.unit_price || 0}
            onChange={(e) => handleUpdateItem(index, 'unit_price', parseFloat(e.target.value) || 0)}
            sx={{ width: 100 }}
            inputProps={{ min: 0, step: 0.01 }}
          />
        )}
      </TableCell>
      {mode === 'edit' && (
        <TableCell align="right">
          <TextField
            size="small"
            type="number"
            value={item.custom_taxable_amount !== undefined && item.custom_taxable_amount !== null 
              ? item.custom_taxable_amount 
              : ((item.unit_price || 0) * (item.quantity || 1))}
            onChange={(e) => handleUpdateItem(index, 'custom_taxable_amount', parseFloat(e.target.value) || 0)}
            sx={{ 
              width: 120,
              '& .MuiInputBase-input': {
                fontWeight: item.is_custom_taxable ? 'bold' : 'normal',
                color: item.is_custom_taxable ? 'warning.main' : 'inherit'
              }
            }}
            inputProps={{ min: 0, step: 0.01 }}
            placeholder="Φορολογητέο"
            helperText={item.is_custom_taxable ? "Custom" : ""}
            FormHelperTextProps={{
              sx: { 
                fontSize: '0.65rem',
                color: 'warning.main',
                fontStyle: 'italic'
              }
            }}
          />
        </TableCell>
      )}
      <TableCell align="right">
        <TextField
          size="small"
          type="number"
          value={item.vat_rate || 24}
          onChange={(e) => handleUpdateItem(index, 'vat_rate', parseFloat(e.target.value) || 24)}
          sx={{ width: 80 }}
          inputProps={{ min: 0, max: 100, step: 0.01 }}
        />
      </TableCell>
      <TableCell align="right">
        <Typography variant="body2" fontWeight="medium">
          {invoiceService.formatCurrency(item.total_price || calculateItemTotal(item))}
        </Typography>
        {item.vat_amount !== undefined && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            (ΦΠΑ: {invoiceService.formatCurrency(item.vat_amount)})
          </Typography>
        )}
      </TableCell>
      {mode !== 'edit' && (
        <TableCell align="right">
          <IconButton
            size="small"
            onClick={() => handleRemoveItem(index)}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </TableCell>
      )}
      {mode === 'edit' && (
        <TableCell align="center">
          {grouped ? (
            <Tooltip title="Διάσπαση σε ξεχωριστές γραμμές">
              <IconButton size="small" onClick={() => handleUngroup(item.group_key)}>
                <CallSplitIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Επιλογή για ομαδοποίηση">
              <Checkbox
                size="small"
                checked={groupSelection.includes(index)}
                onChange={() => handleToggleGroupSelection(index)}
              />
            </Tooltip>
          )}
        </TableCell>
      )}
    </TableRow>
  );

  const calculateTotals = () => {
    if (mode === 'createFromServices') {
      // Πιστωτικό: ποσά από το επιλεγμένο τιμολόγιο (αρνητικά)
      if (formData.invoice_type === 'cancel_invoice' && selectedReferenceInvoiceId) {
        const inv = creditableInvoices.find(i => i.id === selectedReferenceInvoiceId);
        if (inv) {
          const sub = Number(inv.subtotal) || 0;
          const vat = Number(inv.vat_amount) || 0;
          const tot = Number(inv.total_amount) || 0;
          return {
            subtotal: -sub,
            vatAmount: -vat,
            withholdingTax: 0,
            withholdingRate: 0,
            netAmount: -sub,
            total: -tot
          };
        }
      }
      // Calculate from selected services
      const subtotal = selectedServices.reduce((sum, service) => {
        const val = (Number(service?.price) || 0) * (Number(service?.quantity) || 1);
        return sum + (Number.isFinite(val) ? val : 0);
      }, 0);
      
      // ΦΠΑ: χρησιμοποιούμε το default_vat_rate από το form (όχι το service.vat_rate)
      const vatRate = (formData.default_vat_rate !== undefined && formData.default_vat_rate !== null) ? Number(formData.default_vat_rate) : 24;
      const vatAmount = (Number.isFinite(subtotal) ? subtotal : 0) * ((Number.isFinite(vatRate) ? vatRate : 24) / 100);
      
      // Παρακράτηση: υπολογίζεται βάσει του invoice_type
      let withholdingTax = 0;
      let withholdingRate = 0;
      if (formData.invoice_type === 'service_invoice') {
        // Με παρακράτηση: 20% επί του καθαρού
        withholdingRate = 20;
        withholdingTax = subtotal * (withholdingRate / 100);
      } else if (formData.invoice_type === 'service_invoice_no_holdings') {
        // Χωρίς παρακράτηση
        withholdingRate = 0;
        withholdingTax = 0;
      }
      // Για sales_invoice, cancel_invoice, receipt: χωρίς παρακράτηση
      
      // Καθαρό ποσό μετά παρακράτησης
      const netAmount = subtotal - withholdingTax;
      
      // Σύνολο: Καθαρό + ΦΠΑ (ή Καθαρό μετά παρακράτησης + ΦΠΑ)
      // Στην Ελλάδα: Total = Subtotal + VAT - Withholding
      const total = subtotal + vatAmount - withholdingTax;
      
      return { 
        subtotal, 
        vatAmount, 
        withholdingTax,
        withholdingRate,
        netAmount,
        total 
      };
    } else {
      // Calculate from invoice items
      // Subtotal: πάντα unit_price * quantity (καθαρό κόστος)
      const subtotal = formData.invoice_items.reduce((sum, item) => {
        const val = (Number(item?.unit_price) || 0) * (Number(item?.quantity) || 1);
        return sum + (Number.isFinite(val) ? val : 0);
      }, 0);
      // VAT Amount: χρησιμοποιούμε το vat_amount από το item αν υπάρχει και είναι έγκυρο, αλλιώς υπολογίζουμε
      const vatAmount = formData.invoice_items.reduce((sum, item) => {
        const v = item?.vat_amount;
        const isValidVat = typeof v === 'number' && Number.isFinite(v);
        if (isValidVat) {
          return sum + v;
        }
        const itemSubtotal = (Number(item?.unit_price) || 0) * (Number(item?.quantity) || 1);
        const calcVat = itemSubtotal * ((Number(item?.vat_rate) || 24) / 100);
        return sum + (Number.isFinite(calcVat) ? calcVat : 0);
      }, 0);
      const total = (Number.isFinite(subtotal) ? subtotal : 0) + (Number.isFinite(vatAmount) ? vatAmount : 0);
      return { 
        subtotal, 
        vatAmount, 
        withholdingTax: 0,
        withholdingRate: 0,
        netAmount: subtotal,
        total 
      };
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      
      if (mode === 'createFromServices') {
        if (formData.invoice_type === 'cancel_invoice' && selectedReferenceInvoiceId) {
          // Πιστωτικό συσχετιζόμενο - δημιουργία από επιλεγμένο τιμολόγιο
          await invoiceService.createCreditNote(selectedReferenceInvoiceId);
        } else if (formData.invoice_type !== 'cancel_invoice') {
          // Create invoice from services - use regular create endpoint with service_ids
          const invoiceData = {
            client_id: formData.client_id,
            invoice_type: formData.invoice_type,
            title: formData.title,
            issue_date: formData.issue_date,
            // Alternative billing information
            client_billing_info_id: formData.client_billing_info_id ? formData.client_billing_info_id : null,
            billing_vat_number: formData.billing_vat_number || null,
            billing_tax_office: formData.billing_tax_office || null,
            billing_business_name: formData.billing_business_name || null,
            billing_profession: formData.billing_profession || null,
            billing_address: formData.billing_address || null,
            billing_postal_code: formData.billing_postal_code || null,
            payment_method: formData.payment_method,
            notes: formData.notes,
            service_ids: selectedServices.map(s => s.id),
            vat_rate: formData.default_vat_rate !== undefined && formData.default_vat_rate !== null ? String(formData.default_vat_rate) : '24' // Backend expects VATRate enum (string values)
          };

          // Ομαδοποίηση σε μία γραμμή (αν ενεργή και ≥2 υπηρεσίες)
          if (mergeServices && selectedServices.length >= 2) {
            invoiceData.merge_title = (mergeTitle || '').trim() || getDefaultMergeTitle();
          }
          
          await invoiceService.createInvoice(invoiceData);
        } else {
          throw new Error('Επιλέξτε τιμολόγιο για πίστωση');
        }
      } else if (mode === 'edit') {
        // Update existing invoice - send allowed fields + invoice_items if modified
        const updateData = {
          title: formData.title,
          payment_method: formData.payment_method,
          notes: formData.notes,
          // Alternative billing information
          client_billing_info_id: formData.client_billing_info_id ? formData.client_billing_info_id : null,
          billing_vat_number: formData.billing_vat_number || null,
          billing_tax_office: formData.billing_tax_office || null,
          billing_business_name: formData.billing_business_name || null,
          billing_profession: formData.billing_profession || null,
          billing_address: formData.billing_address || null,
          billing_postal_code: formData.billing_postal_code || null
        };
        
        // Include invoice_items if they exist (for editing item prices/quantities)
        if (formData.invoice_items && formData.invoice_items.length > 0) {
          updateData.invoice_items = formData.invoice_items.map(item => {
            const itemData = {
              service_id: item.service_id || null,
              name: item.name,
              description: item.description || null,
              quantity: item.quantity || 1,
              unit_price: item.unit_price || 0,
              vat_rate: item.vat_rate || 24,
              sort_order: item.sort_order || 0,
              group_key: item.group_key || null,
              group_title: item.group_title || null
            };
            // Αν είναι custom taxable amount, στείλε το custom_taxable_amount (το backend θα υπολογίσει το vat_amount)
            // Στείλε το custom_taxable_amount αν έχει οριστεί και είναι > 0
            if (item.custom_taxable_amount !== undefined && item.custom_taxable_amount !== null && item.custom_taxable_amount > 0) {
              itemData.custom_taxable_amount = item.custom_taxable_amount;
            }
            return itemData;
          });
        }
        
        await invoiceService.updateInvoice(invoice.id, updateData);
      } else {
        // Create new invoice with manual items - don't send invoice_number (auto-generated)
        const createData = { ...formData };
        delete createData.invoice_number; // Let backend auto-generate
        await invoiceService.createInvoice(createData);
      }

      onSuccess();
    } catch (err) {
      console.error('Error saving invoice:', err);
      if (err.response?.data?.detail) {
        setErrors({ general: err.response.data.detail });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const getTitle = () => {
    if (mode === 'edit') return 'Επεξεργασία Τιμολογίου';
    if (mode === 'createFromServices') return 'Δημιουργία Τιμολογίου από Υπηρεσίες';
    return 'Νέο Τιμολόγιο';
  };

  const totals = calculateTotals();

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={el}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '80vh' }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              {getTitle()}
            </Typography>
            <Button
              startIcon={<CloseIcon />}
              onClick={onClose}
              color="inherit"
            >
              Κλείσιμο
            </Button>
          </Box>
        </DialogTitle>

        <DialogContent>
          {errors.general && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.general}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Basic Information */}
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Στοιχεία Τιμολογίου
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <ClientSelect
                      name="client_id"
                      label="Πελάτης"
                      value={formData.client_id || ''}
                      onChange={(name, value) => handleInputChange('client_id', value)}
                      error={!!errors.client_id}
                      helperText={errors.client_id}
                      required
                      disabled={mode === 'edit'}
                      placeholder="Επιλέξτε ή αναζητήστε πελάτη..."
                    />
                  </Grid>

                  {mode === 'edit' && formData.invoice_number && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Αριθμός Τιμολογίου"
                        value={formData.invoice_number || ''}
                        disabled
                      />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Τύπος</InputLabel>
                      <Select
                        value={formData.invoice_type}
                        onChange={(e) => handleInputChange('invoice_type', e.target.value)}
                        label="Τύπος"
                        disabled={mode === 'edit'}
                      >
                        <MenuItem value="service_invoice">Παροχής Υπηρεσιών (με παρακράτηση)</MenuItem>
                        <MenuItem value="service_invoice_no_holdings">Παροχής Υπηρεσιών (χωρίς παρακράτηση)</MenuItem>
                        <MenuItem value="sales_invoice">Πώλησης</MenuItem>
                        <MenuItem value="cancel_invoice">Πιστωτικό</MenuItem>
                        <MenuItem value="receipt">Απόδειξη Είσπραξης</MenuItem>
                        <MenuItem value="proforma_invoice">Προτιμολόγιο</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>ΦΠΑ</InputLabel>
                      <Select
                        value={formData.default_vat_rate !== undefined && formData.default_vat_rate !== null ? formData.default_vat_rate : 24}
                        onChange={(e) => handleInputChange('default_vat_rate', parseFloat(e.target.value))}
                        label="ΦΠΑ"
                      >
                        <MenuItem value={0}>0%</MenuItem>
                        <MenuItem value={6}>6%</MenuItem>
                        <MenuItem value={12}>12%</MenuItem>
                        <MenuItem value={24}>24%</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Τίτλος"
                      value={formData.title || ''}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      error={!!errors.title}
                      helperText={errors.title}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <DatePicker
                      label="Ημ. Έκδοσης *"
                      value={formData.issue_date ? new Date(formData.issue_date) : null}
                      onChange={(date) => handleInputChange('issue_date', date?.toISOString().split('T')[0])}
                      disabled={mode === 'edit'}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.issue_date,
                          helperText: errors.issue_date
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth>
                      <InputLabel>Μέθοδος Πληρωμής</InputLabel>
                      <Select
                        value={formData.payment_method}
                        onChange={(e) => handleInputChange('payment_method', e.target.value)}
                        label="Μέθοδος Πληρωμής"
                      >
                        <MenuItem value="bank_transfer">Τραπεζική Μεταφορά</MenuItem>
                        <MenuItem value="cash">Μετρητά</MenuItem>
                        <MenuItem value="card">Κάρτα</MenuItem>
                        <MenuItem value="check">Επιταγή</MenuItem>
                        <MenuItem value="other">Άλλο</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Σημειώσεις"
                      value={formData.notes || ''}
                      onChange={(e) => handleInputChange('notes', e.target.value)}
                      error={!!errors.notes}
                      helperText={errors.notes}
                    />
                  </Grid>

                  {/* Billing Information Selection */}
                  {formData.client_id && (
                    <Grid item xs={12}>
                      <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                          Στοιχεία Τιμολόγησης
                        </Typography>
                        
                        {/* Dropdown for saved billing info */}
                        {clientBillingInfo.length > 0 ? (
                          <FormControl fullWidth size="medium" sx={{ mb: 2 }}>
                            <InputLabel>Επιλογή Στοιχείων Τιμολόγησης</InputLabel>
                            <Select
                              value={formData.client_billing_info_id || ''}
                              onChange={(e) => handleBillingInfoSelect(e.target.value)}
                              label="Επιλογή Στοιχείων Τιμολόγησης"
                            >
                              <MenuItem value="">
                                <em>Χρήση Προεπιλογής (Default)</em>
                              </MenuItem>
                              {clientBillingInfo.map((info) => (
                                <MenuItem key={info.id} value={info.id}>
                                  {info.is_default && '⭐ '}
                                  {info.label || `Σετ ${info.id}`}
                                  {info.is_default && ' (Προεπιλογή)'}
                                  {info.business_name && ` - ${info.business_name}`}
                                  {info.vat_number && ` [ΑΦΜ: ${info.vat_number}]`}
                                </MenuItem>
                              ))}
                            </Select>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              Επιλέξτε ποιο σετ φορολογικών στοιχείων θέλετε να χρησιμοποιήσετε για αυτό το τιμολόγιο
                            </Typography>
                          </FormControl>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Ο πελάτης δεν έχει αποθηκευμένα στοιχεία τιμολόγησης. Θα χρησιμοποιηθούν τα βασικά στοιχεία του πελάτη.
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  )}

                  {/* Alternative/Custom Billing Information */}
                  <Grid item xs={12}>
                    <Accordion expanded={showBillingInfo} onChange={(e, expanded) => setShowBillingInfo(expanded)}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">
                          Προσαρμοσμένα Στοιχεία Τιμολόγησης (Προαιρετικά)
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                          Αν θέλετε να χρησιμοποιήσετε διαφορετικά στοιχεία από τα αποθηκευμένα, συμπληρώστε τα παρακάτω πεδία.
                          Αυτά θα έχουν προτεραιότητα έναντι της επιλογής από το dropdown.
                        </Typography>
                        <Grid container spacing={2}>
                          {formData.client_billing_info_id && (
                            <Grid item xs={12}>
                              <Alert severity="info" sx={{ mb: 2 }}>
                                Έχετε επιλέξει αποθηκευμένα στοιχεία από το dropdown. Αν θέλετε να αλλάξετε, επιλέξτε διαφορετικά στοιχεία ή αλλάξτε το dropdown σε "Χρήση Προεπιλογής" για να συμπληρώσετε custom πεδία.
                              </Alert>
                            </Grid>
                          )}
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Επωνυμία"
                              value={formData.billing_business_name || ''}
                              onChange={(e) => handleInputChange('billing_business_name', e.target.value)}
                              size="small"
                              disabled={!!formData.client_billing_info_id}
                              helperText={formData.client_billing_info_id ? 'Συμπληρώθηκε από την επιλογή' : ''}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="Επάγγελμα"
                              value={formData.billing_profession || ''}
                              onChange={(e) => handleInputChange('billing_profession', e.target.value)}
                              size="small"
                              disabled={!!formData.client_billing_info_id}
                              helperText={formData.client_billing_info_id ? 'Συμπληρώθηκε από την επιλογή' : ''}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="ΑΦΜ"
                              value={formData.billing_vat_number || ''}
                              onChange={(e) => handleInputChange('billing_vat_number', e.target.value)}
                              size="small"
                              disabled={!!formData.client_billing_info_id}
                              helperText={formData.client_billing_info_id ? 'Συμπληρώθηκε από την επιλογή' : ''}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth
                              label="ΔΟΥ"
                              value={formData.billing_tax_office || ''}
                              onChange={(e) => handleInputChange('billing_tax_office', e.target.value)}
                              size="small"
                              disabled={!!formData.client_billing_info_id}
                              helperText={formData.client_billing_info_id ? 'Συμπληρώθηκε από την επιλογή' : ''}
                            />
                          </Grid>
                          <Grid item xs={12} sm={8}>
                            <TextField
                              fullWidth
                              label="Διεύθυνση"
                              value={formData.billing_address || ''}
                              onChange={(e) => handleInputChange('billing_address', e.target.value)}
                              size="small"
                              multiline
                              rows={2}
                              disabled={!!formData.client_billing_info_id}
                              helperText={formData.client_billing_info_id ? 'Συμπληρώθηκε από την επιλογή' : ''}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <TextField
                              fullWidth
                              label="ΤΚ"
                              value={formData.billing_postal_code || ''}
                              onChange={(e) => handleInputChange('billing_postal_code', e.target.value)}
                              size="small"
                              disabled={!!formData.client_billing_info_id}
                              helperText={formData.client_billing_info_id ? 'Συμπληρώθηκε από την επιλογή' : ''}
                            />
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                </Grid>
              </Box>
            </Grid>

            {/* Service Selection or Invoice Items or Creditable Invoices (for πιστωτικό) */}
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 3 }}>
                {mode === 'createFromServices' && formData.invoice_type === 'cancel_invoice' ? (
                  <>
                    <Typography variant="h6" mb={2}>
                      Επιλογή Τιμολογίου για Πίστωση
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Επιλέξτε το τιμολόγιο που θέλετε να κάνετε πίστωση. Εμφανίζονται μόνο τιμολόγια που έχουν ανέβει στο MyData.
                    </Typography>
                    {loading ? (
                      <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress />
                      </Box>
                    ) : !formData.client_id ? (
                      <Alert severity="info">Επιλέξτε πρώτα έναν πελάτη</Alert>
                    ) : creditableInvoices.length === 0 ? (
                      <Alert severity="info">
                        Δεν βρέθηκαν τιμολόγια για πίστωση. Τα τιμολόγια πρέπει να έχουν ανέβει στο MyData.
                      </Alert>
                    ) : (
                      <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                        <Table stickyHeader size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell padding="checkbox" />
                              <TableCell>Αριθμός</TableCell>
                              <TableCell>Ημερομηνία</TableCell>
                              <TableCell align="right">Σύνολο</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {creditableInvoices.map((inv) => (
                              <TableRow
                                key={inv.id}
                                hover
                                selected={selectedReferenceInvoiceId === inv.id}
                                onClick={() => setSelectedReferenceInvoiceId(inv.id)}
                                sx={{ cursor: 'pointer' }}
                              >
                                <TableCell padding="checkbox">
                                  <Radio
                                    checked={selectedReferenceInvoiceId === inv.id}
                                    onChange={() => setSelectedReferenceInvoiceId(inv.id)}
                                  />
                                </TableCell>
                                <TableCell>{inv.invoice_number}</TableCell>
                                <TableCell>{invoiceService.formatDate(inv.issue_date)}</TableCell>
                                <TableCell align="right">{invoiceService.formatCurrency(inv.total_amount)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </>
                ) : mode === 'createFromServices' ? (
                  <>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <Typography variant="h6">
                        Επιλογή Υπηρεσιών
                      </Typography>
                      {clientServices.length > 0 && (
                        <Button
                          size="small"
                          onClick={handleSelectAll}
                          startIcon={<CheckIcon />}
                        >
                          {selectedServices.length === clientServices.length ? 'Αποεπιλογή Όλων' : 'Επιλογή Όλων'}
                        </Button>
                      )}
                    </Box>

                    {loading ? (
                      <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress />
                      </Box>
                    ) : clientServices.length === 0 ? (
                      <Alert severity="info">
                        {formData.client_id ? 'Δεν υπάρχουν μη τιμολογημένες υπηρεσίες για αυτόν τον πελάτη' : 'Επιλέξτε πρώτα έναν πελάτη'}
                      </Alert>
                    ) : (
                      <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                        <Table stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={selectedServices.length === clientServices.length && clientServices.length > 0}
                                  indeterminate={selectedServices.length > 0 && selectedServices.length < clientServices.length}
                                  onChange={handleSelectAll}
                                />
                              </TableCell>
                              <TableCell>Υπηρεσία</TableCell>
                              <TableCell align="right">Τιμή</TableCell>
                              <TableCell align="right">ΦΠΑ</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {clientServices.map((service) => {
                              const isSelected = selectedServices.some(s => s.id === service.id);
                              const selectionTitle = formatServiceSelectionTitle(service);
                              return (
                                <TableRow
                                  key={service.id}
                                  hover
                                  onClick={() => handleServiceToggle(service)}
                                  sx={{ cursor: 'pointer' }}
                                >
                                  <TableCell padding="checkbox">
                                    <Checkbox
                                      checked={isSelected}
                                      onChange={() => handleServiceToggle(service)}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight="medium" sx={{ whiteSpace: 'normal' }}>
                                      {selectionTitle}
                                    </Typography>
                                    {service.description && (
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        {service.description}
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2">
                                      {invoiceService.formatCurrency(service.price * service.quantity)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2">
                                      {service.vat_rate || 24}%
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}

                    {/* Ομαδοποίηση επιλεγμένων υπηρεσιών σε μία γραμμή τιμολογίου */}
                    {canMergeServices && (
                      <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={mergeServices}
                              onChange={(e) => handleMergeToggle(e.target.checked)}
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                Ομαδοποίηση σε μία γραμμή τιμολογίου
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Οι {selectedServices.length} υπηρεσίες θα εμφανιστούν ως μία γραμμή με ενιαίο τίτλο.
                                Οι υπηρεσίες παραμένουν συνδεδεμένες — υπόλοιπα και ανανεώσεις ανά υπηρεσία δεν αλλάζουν.
                              </Typography>
                            </Box>
                          }
                        />
                        {mergeServices && (
                          <TextField
                            fullWidth
                            size="small"
                            sx={{ mt: 2 }}
                            label="Τίτλος γραμμής"
                            value={mergeTitle}
                            onChange={(e) => setMergeTitle(e.target.value)}
                            placeholder={getDefaultMergeTitle()}
                            helperText={`Σύνολο γραμμής (καθαρό): ${invoiceService.formatCurrency(
                              selectedServices.reduce(
                                (sum, s) => sum + ((Number(s?.price) || 0) * (Number(s?.quantity) || 1)),
                                0
                              )
                            )}`}
                          />
                        )}
                      </Paper>
                    )}
                  </>
                ) : (
                  <>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <Typography variant="h6">
                        Γραμμές Τιμολογίου
                      </Typography>
                      {mode !== 'edit' && (
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={handleAddItem}
                        >
                          Προσθήκη
                        </Button>
                      )}
                      {mode === 'edit' && (
                        <Typography variant="caption" color="text.secondary">
                          Μπορείτε να επεξεργαστείτε: Ποσότητα, Φορολογητέο Ποσό (custom), ΦΠΑ%. Η Τιμή μονάδας (καθαρό κόστος) παραμένει αμετάβλητη.
                        </Typography>
                      )}
                    </Box>

                    {formData.invoice_items.length === 0 ? (
                      <Alert severity="info">
                        Δεν υπάρχουν γραμμές τιμολογίου.
                      </Alert>
                    ) : (
                      <>
                      {mode === 'edit' && groupSelection.length >= 2 && (
                        <Box mb={1} display="flex" justifyContent="flex-end">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={handleGroupSelected}
                          >
                            Ομαδοποίηση {groupSelection.length} γραμμών σε μία
                          </Button>
                        </Box>
                      )}
                      <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                        <Table stickyHeader>
                          <TableHead>
                            <TableRow>
                              <TableCell>Υπηρεσία</TableCell>
                              <TableCell align="right">Ποσότητα</TableCell>
                              <TableCell align="right">Τιμή</TableCell>
                              {mode === 'edit' && <TableCell align="right">Φορολογητέο</TableCell>}
                              <TableCell align="right">ΦΠΑ%</TableCell>
                              <TableCell align="right">Σύνολο</TableCell>
                              {mode !== 'edit' && <TableCell width={50}></TableCell>}
                              {mode === 'edit' && <TableCell width={50}></TableCell>}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {buildInvoiceRows(formData.invoice_items).map((row) => {
                              if (row.type === 'group') {
                                const isExpanded = !!expandedGroups[row.groupKey];
                                const summary = summarizeGroup(row.items);
                                const groupTitle = row.items[0].item.group_title || row.items[0].item.name || '';
                                return (
                                  <React.Fragment key={row.key}>
                                    <TableRow sx={{ backgroundColor: 'action.hover' }}>
                                      <TableCell>
                                        <Box display="flex" alignItems="flex-start" gap={0.5}>
                                          <IconButton size="small" onClick={() => toggleGroupExpanded(row.groupKey)}>
                                            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                                          </IconButton>
                                          <Box flex={1}>
                                            <TextField
                                              size="small"
                                              fullWidth
                                              value={groupTitle}
                                              onChange={(e) => handleUpdateGroupTitle(row.groupKey, e.target.value)}
                                              placeholder="Τίτλος γραμμής"
                                            />
                                            <Typography variant="caption" color="text.secondary">
                                              {row.items.length} υπηρεσίες σε μία γραμμή
                                            </Typography>
                                          </Box>
                                        </Box>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography variant="body2">1</Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography variant="body2" color="text.secondary">
                                          {invoiceService.formatCurrency(summary.net)}
                                        </Typography>
                                      </TableCell>
                                      {mode === 'edit' && (
                                        <TableCell align="right">
                                          <Typography variant="body2">
                                            {invoiceService.formatCurrency(summary.net)}
                                          </Typography>
                                        </TableCell>
                                      )}
                                      <TableCell align="right">
                                        <Typography variant="body2">
                                          {summary.vatRate === null ? '—' : `${summary.vatRate}%`}
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="right">
                                        <Typography variant="body2" fontWeight="medium">
                                          {invoiceService.formatCurrency(summary.total)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                          (ΦΠΑ: {invoiceService.formatCurrency(summary.vat)})
                                        </Typography>
                                      </TableCell>
                                      <TableCell align="center">
                                        <Tooltip title="Διάσπαση σε ξεχωριστές γραμμές">
                                          <IconButton size="small" onClick={() => handleUngroup(row.groupKey)}>
                                            <CallSplitIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </TableCell>
                                    </TableRow>
                                    {isExpanded && row.items.map(({ item, index }) => renderItemRow(item, index, { grouped: true }))}
                                  </React.Fragment>
                                );
                              }
                              return renderItemRow(row.item, row.index);
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      </>
                    )}
                  </>
                )}

                {/* Totals */}
                {(formData.invoice_items.length > 0 || selectedServices.length > 0 || (mode === 'createFromServices' && formData.invoice_type === 'cancel_invoice' && selectedReferenceInvoiceId)) && (
                  <Card variant="outlined" sx={{ mt: 2 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Υπολογισμοί
                      </Typography>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2">Καθαρό:</Typography>
                        <Typography variant="body2">
                          {invoiceService.formatCurrency(totals.subtotal)}
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2">
                          ΦΠΑ ({mode === 'createFromServices' ? (formData.default_vat_rate !== undefined && formData.default_vat_rate !== null ? formData.default_vat_rate : 24) : '24'}%):
                        </Typography>
                        <Typography variant="body2">
                          {invoiceService.formatCurrency(totals.vatAmount)}
                        </Typography>
                      </Box>
                      {mode === 'createFromServices' && totals.withholdingTax > 0 && (
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography variant="body2" color="warning.main">
                            Παρακράτηση ({totals.withholdingRate}%):
                          </Typography>
                          <Typography variant="body2" color="warning.main">
                            -{invoiceService.formatCurrency(totals.withholdingTax)}
                          </Typography>
                        </Box>
                      )}
                      {mode === 'createFromServices' && totals.withholdingTax > 0 && (
                        <Box display="flex" justifyContent="space-between" mb={1}>
                          <Typography variant="body2" color="text.secondary">
                            Καθαρό μετά παρακράτησης:
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {invoiceService.formatCurrency(totals.netAmount)}
                          </Typography>
                        </Box>
                      )}
                      <Divider sx={{ my: 1 }} />
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="h6">Σύνολο:</Typography>
                        <Typography variant="h6" color="primary">
                          {invoiceService.formatCurrency(totals.total)}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                )}
              </Box>
            </Grid>
          </Grid>

          {/* Selected Services Summary for createFromServices mode */}
          {mode === 'createFromServices' && selectedServices.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                Σύνοψη Επιλεγμένων Υπηρεσιών
              </Typography>

              {mergeServices && selectedServices.length >= 2 ? (
                <>
                  <Chip
                    label={`${(mergeTitle || getDefaultMergeTitle())} · ${invoiceService.formatCurrency(
                      selectedServices.reduce(
                        (sum, s) => sum + ((Number(s?.price) || 0) * (Number(s?.quantity) || 1)),
                        0
                      )
                    )}`}
                    color="primary"
                    sx={{ maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }}
                  />
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                    Περιλαμβάνει: {selectedServices.map((s) => s.name).join(', ')}
                  </Typography>
                </>
              ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {selectedServices
                  .filter((service, index, self) => 
                    index === self.findIndex((s) => s.id === service.id)
                  )
                  .map((service, index) => {
                    const title = formatServiceSelectionTitle(service);
                    const priceLine = invoiceService.formatCurrency(service.price * service.quantity);
                    const chipLabel = `${title} · ${priceLine}`;
                    return (
                      <Tooltip key={`selected-${service.id}-${index}`} title={chipLabel}>
                        <Chip
                          label={chipLabel}
                          onDelete={() => handleServiceToggle(service)}
                          color="primary"
                          variant="outlined"
                          sx={{
                            maxWidth: '100%',
                            height: 'auto',
                            '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 }
                          }}
                        />
                      </Tooltip>
                    );
                  })}
              </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={onClose} disabled={submitting}>
            Ακύρωση
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              submitting || 
              !formData.client_id || 
              (mode === 'createFromServices' && formData.invoice_type === 'cancel_invoice' && !selectedReferenceInvoiceId) ||
              (mode === 'createFromServices' && formData.invoice_type !== 'cancel_invoice' && selectedServices.length === 0) ||
              (mode === 'create' && formData.invoice_items.length === 0)
            }
            startIcon={submitting ? <CircularProgress size={20} /> : <SaveIcon />}
          >
            {submitting ? 'Αποθήκευση...' : (mode === 'edit' ? 'Ενημέρωση' : 'Δημιουργία')}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default InvoiceFormModal;